"use client";

import { useTheme } from "next-themes";
import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { type ExternalToast, Toaster as Sonner, toast as sonnerToast, type ToasterProps } from "sonner";

import { type ErrorToastAction, ErrorToastCard, toPlainText } from "./error-toast";

const resolveAction = (action: ExternalToast["action"]): ErrorToastAction | undefined => {
  if (!action || typeof action !== "object" || !("label" in action) || !("onClick" in action)) {
    return undefined;
  }

  return {
    label: String(action.label),
    onClick: action.onClick,
  };
};

const toastError = (message: ReactNode, data?: ExternalToast) => {
  const title = toPlainText(message);
  const rawDescription = data?.description;
  const descriptionNode = typeof rawDescription === "function" ? (rawDescription as () => ReactNode)() : rawDescription;
  const description = toPlainText(descriptionNode ?? null) ?? undefined;
  const action = resolveAction(data?.action);

  if (title === null) {
    return sonnerToast.error(message, {
      ...data,
      duration: data?.duration ?? Infinity,
      closeButton: data?.closeButton ?? true,
    });
  }

  const { description: _ignoredDescription, action: _ignoredAction, ...rest } = data ?? {};

  return sonnerToast.custom(
    (id) => <ErrorToastCard toastId={id} title={title} description={description} action={action} />,
    {
      ...rest,
      duration: rest.duration ?? Infinity,
      // Own Dismiss control — prevent Sonner from treating clicks as dismiss/swipe.
      dismissible: false,
      unstyled: true,
      className: "!border-none !bg-transparent !p-0 !shadow-none",
    },
  );
};

const toast = Object.assign(
  (message: Parameters<typeof sonnerToast>[0], data?: ExternalToast) => sonnerToast(message, data),
  {
    success: sonnerToast.success,
    info: sonnerToast.info,
    warning: sonnerToast.warning,
    loading: sonnerToast.loading,
    message: sonnerToast.message,
    promise: sonnerToast.promise,
    custom: sonnerToast.custom,
    dismiss: sonnerToast.dismiss,
    getHistory: sonnerToast.getHistory,
    getToasts: sonnerToast.getToasts,
    error: toastError,
  },
);

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  // Portal to document.body so toasts aren't trapped under dialog overlays.
  // Dialogs portal to body at z-50; a toaster nested in React tree loses that fight
  // even with a higher local z-index because of ancestor stacking contexts.
  return createPortal(
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          zIndex: 200,
        } as CSSProperties
      }
      {...props}
    />,
    document.body,
  );
};

export { Toaster, toast };
